/**
 * Appending a VAT rate from a screen (ADR-0082).
 *
 * A rate is **basis points**, an integer from 0 to 10000: 1100 is 11%, and 0 is a zero-rated
 * supply stated rather than assumed. It is a JSON number, unlike an FX rate: it is not money and
 * not a rational, and 10000 is far inside what a double holds exactly. The screen takes a
 * percentage and converts it to basis points with string arithmetic, so no float ever holds it.
 *
 * **Keyed by the Merchant's country.** For a business customer the place of supply is where the
 * customer is established, and `merchants.country` is the legal entity's country, `LB` or `SY`.
 *
 * **Instants are sent as typed**, for `fx.ts`'s reason: a browser's parser applies the viewer's
 * zone to a string without one, and a rate effective at the wrong hour taxes the wrong charges.
 *
 * `basis` is the law or decree the rate stands on, recorded because a charge cites the row, and
 * the basis is what answers "why this rate" a year later.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/** The countries whose Merchants this platform serves, as `merchants.country` holds them. */
export const VAT_COUNTRIES = ['LB', 'SY'];
/** 10000 basis points is 100%, the kernel's `TAX_RATE_BPS_MAX` and the table's CHECK. */
export const VAT_RATE_BPS_MAX = 10_000;
const instant = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/u, 'iso_instant');
export const appendVatRateSchema = z
    .object({
    country: z.enum(VAT_COUNTRIES),
    rateBps: z.number().int().min(0).max(VAT_RATE_BPS_MAX),
    effectiveFrom: instant,
    basis: z.string().trim().min(1).max(300),
    owner: z.string().trim().min(1).max(200),
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const appendVatRateRendered = renderedText(appendVatRateSchema, {
    prose: ['basis', 'owner'],
    exempt: {
        effectiveFrom: 'an ISO instant ending in Z (iso_instant), which holds no phone-shaped digit run',
        note: 'stored only in the config.vat_rates.amended event payload; no view renders it',
    },
});
//# sourceMappingURL=vat.js.map